import { extractAndExcludeCurlyBrackets, findFirstDuplicateOptionIndex } from "lib/utils";
import {
  meetingTypes,
  minimumCountOfOptions,
  surveyQuestionTypes,
  TOUCH_POINT_TYPES,
} from "./constant";
import { mandatoryPlaceholders } from "constants";

export const handleInvalidPlaceHolderLength = ({ mandatoryPlaceholders ,extractedPlaceholders }) => {
  const invalidPlaceholder = extractedPlaceholders.filter(
    (placeholder) => !mandatoryPlaceholders.includes(placeholder)
  );
  if (invalidPlaceholder.length > 0) {
      return `Invalid placeholders detected: ${invalidPlaceholder.join(
        ", "
      )}. Please use valid placeholders.`
  }
  return false;
}

export const validateTouchPoint = (touchPoint, isCompanyAdmin, isPostJoiningJourney = false) => {
  let errors = {
    emailContent: {},
    hubContent: {},
  };

  if (touchPoint.touchPointName.trim() === "") {
    errors.touchPointName = "Touch point name is required";
  }

  if ((touchPoint?.lockedTouchpointTitle || "").length > 255) {
    errors.lockedTouchpointTitle =
      "Locked touchpoint title must be at most 255 characters";
  }

  if (touchPoint.emailContent.notificationViaEmail) {
    if (touchPoint.emailContent.design === "") {
      errors.emailContent.design = "Design is required";
    }
    if (touchPoint.emailContent.fromEmail === "" && isCompanyAdmin) {
      errors.emailContent.fromEmail = "From email is required";
    }
    if (!touchPoint.emailContent.html || touchPoint.emailContent.html === "") {
      errors.emailContent.html = "HTML is required";
    }else{

      const extractedPlaceholders = extractAndExcludeCurlyBrackets(touchPoint.emailContent?.html);
      const placeholderError = handleInvalidPlaceHolderLength({ mandatoryPlaceholders, extractedPlaceholders });
      if (placeholderError) {
        errors.emailContent.html = placeholderError;
      }
    }
    if (touchPoint.emailContent.subject.trim() === "") {
      errors.emailContent.subject = "Subject is required";
    }
  }

  if (
    touchPoint.touchPointType !== TOUCH_POINT_TYPES.survey &&
    touchPoint.touchPointType !== TOUCH_POINT_TYPES.quiz
  ) {
    if (
      !touchPoint.hubContent.hubTemplate.design ||
      touchPoint.hubContent.hubTemplate.design === ""
    ) {
      errors.hubContent = errors.hubContent || {};
      errors.hubContent.design = "Design is required";
    }
    if (
      !touchPoint.hubContent.hubTemplate.html ||
      touchPoint.hubContent.hubTemplate.html === ""
    ) {
      errors.hubContent = errors.hubContent || {};
      errors.hubContent.html = "HTML is required";
    }else{
      const extractedPlaceholders = extractAndExcludeCurlyBrackets(touchPoint.hubContent?.hubTemplate?.html);
      const placeholderError = handleInvalidPlaceHolderLength({ mandatoryPlaceholders, extractedPlaceholders });
      if (placeholderError) {
        errors.hubContent.html = placeholderError;
      }
    }
  }
  
  if (
    touchPoint.hubContent.daysToExecuteAfterJoining === "" &&
    isPostJoiningJourney
  ) {
    errors.hubContent.minimumDaysToJoin = "Days to execute after official work email added is required";
  }

  if (
    isPostJoiningJourney &&
    touchPoint?.hubContent?.daysToExecuteAfterJoining
  ) {
    const daysValue = Number(touchPoint?.hubContent?.daysToExecuteAfterJoining);
    if (daysValue < 1) {
      errors.hubContent.daysToExecuteAfterJoining = "Entered value must be greater than or equal to 1";
    } else if (!Number.isInteger(daysValue)) {
      errors.hubContent.daysToExecuteAfterJoining = "Entered value must be a whole number";
    }
  }

  if (
    touchPoint.hubContent.minimumDaysToJoin === "" &&
    touchPoint.touchPointType !== TOUCH_POINT_TYPES.meeting
  ) {
    errors.hubContent.minimumDaysToJoin = "Minimum days to join is required";
  }
  if (touchPoint.hubContent.minimumDaysToJoin < 0) {
    errors.hubContent.minimumDaysToJoin =
      "Minimum days to join should be greater than or equal to 0";
  }
  if (touchPoint.touchPointType === TOUCH_POINT_TYPES.survey) {
    const error = validateSurveyQuizTouchPoint(
      touchPoint,
      touchPoint.touchPointType
    );
    if (error !== undefined) {
      errors.hubContent.surveyValidation = error;
    }
  }

  if (touchPoint.touchPointType === TOUCH_POINT_TYPES.meeting) {
    const error = validateMeetingTouchPoint(touchPoint, isCompanyAdmin);
    if (error !== undefined) {
      errors.hubContent.meetingValidation = error;
    }
  }

  if (touchPoint.touchPointType === TOUCH_POINT_TYPES.quiz) {
    const error = validateSurveyQuizTouchPoint(
      touchPoint,
      touchPoint.touchPointType
    );
    if (error !== undefined) {
      errors.hubContent.quizValidation = error;
    }
  }

  if (
    Object.keys(errors.emailContent).length === 0 &&
    Object.keys(errors.hubContent).length === 0 &&
    !errors.touchPointName &&
    !errors.lockedTouchpointTitle
    ) {
    return undefined;
  }

  return errors;
};

export const validateMeetingTouchPoint = (touchPoint, isCompanyAdmin) => {
  let errors = {};
  if (!touchPoint.hubContent.meetingPerson) {
    errors.meetingPerson = "Meeting person is required";
  }
  if (touchPoint.hubContent.meetingType === meetingTypes.inPerson) {
    if (!touchPoint.hubContent.meetingLocation && isCompanyAdmin) {
      errors.meetingLocation = "Meeting location is required";
    }
  }
  if (Object.keys(errors).length === 0) {
    return undefined;
  }
  return errors;
};

export const validateSurveyQuizTouchPoint = (touchPoint, type) => {
  const errors = {};

  const {
    hubContent: {
      questions = [],
      footerContent,
      headerContent,
      headerDescription = "",
      footerDescription = "",
      quiz_questions = []
    } = {},
  } = touchPoint || {};

  const isSurvey = type === "survey";
  const isQuiz = type === "quiz";

  const targetQuestions = isSurvey ? questions : quiz_questions;

  if (!Array.isArray(targetQuestions) || targetQuestions.length === 0) {
    errors.questionLength = `${type} questions are required`;
  } else {
    const questionErrors = validateQuestions(
      targetQuestions,
      touchPoint?.touchPointType
    );
    if (Object.values(questionErrors).some((val) => val !== undefined)) {
      if (isSurvey) {
        errors.questions = questionErrors;
      } else {
        errors.quiz_questions = questionErrors;
      }
    }
  }

  const validateContent = (field, content) => {
    if (!content?.trim() || content === "<p> </p>") {
      errors[field] = `${type} ${field
        .replace(/([A-Z])/g, " $1")
        .toLowerCase()} is required`;
    }
  };

  if (isQuiz) {
    validateContent("headerHtml", headerContent?.html);
    validateContent("footerHtml", footerContent?.html);
  } else {
    validateContent("headerDescription", headerDescription);
    validateContent("footerDescription", footerDescription);
  }

  return Object.keys(errors).length ? errors : undefined;
};

export const validateQuestions = (questions, touchPointType) => {
  let errors = [];
  questions.forEach((question, i) => {
    let error = {};
    if (question.text === "" || !question.text.replace(/\s/g, "")) {
      error.text = "Question is required";
    }
    if (question.type === "") {
      error.type = "Type is required";
    }
    if (
      [
        surveyQuestionTypes.singleSelect,
        surveyQuestionTypes.multiSelect,
      ].includes(question.type)
    ) {
      const selectError = validateSingleSelectQuestion(
        question,
        touchPointType
      );
      if (selectError) {
        error.question = selectError;
      }
    }
    if (
      touchPointType === "survey" &&
      question.additionalTextField
    ) {
      if (
        !question.additionalTextField.title ||
        !question.additionalTextField.title.trim()
      ) {
        error.additionalTextField = {
          title: "Additional field title is required",
        };
      }
    }
    
    if (Object.keys(error).length > 0) {
      errors.push(error);
    } else {
      errors.push(undefined);
    }
  });
  if (errors.length === 0) {
    return undefined;
  }
  return errors;
};

const validateSingleSelectQuestion = (question, touchPointType) => {
  const error = {};
  if (question.options.length < minimumCountOfOptions) {
    error.optionCount = "At least two options are required";
  }
  question?.options?.forEach((option, optionIndex) => {
    const optionValue = option?.value || "";
    if (optionValue === "" || !optionValue.replace(/\s/g, "")) {
      error[`option${optionIndex}`] = "Option is required";
    }
  });

  const firstDuplicateOptionIndex = findFirstDuplicateOptionIndex(question?.options || []);
  if (firstDuplicateOptionIndex !== -1) {
    error[`option${firstDuplicateOptionIndex}`] = "Each option must be unique";
  }

  if (touchPointType === "quiz") {
    const hasCorrectAnswer = question.options.some(
      (option) => option.isCorrect === true
    );
    if (!hasCorrectAnswer) {
      error.isCorrect =
        "At least one option must be marked as the correct answer for a quiz";
    }
  }
  if (Object.keys(error).length > 0) {
    return error;
  }
  return undefined;
};
